import React, { Fragment, useEffect, useRef } from "react";
import { Element } from "react-scroll";
import {
  TextButton,
  IconButton,
  getDataAnalyticsProps,
  Label,
  LabelVariant,
} from "@SeemplicityDev/shared";

interface TitleAction {
  icon: string;
  onClick: () => void;
  tooltipContent: React.ReactNode;
}

interface SectionAction {
  isButton?: boolean;
  icon?: string;
  label?: string;
  onClick?: () => void;
  tooltipContent?: React.ReactNode;
  component?: React.ReactNode;
}

export interface MenuItem {
  id: string;
  isDisabled?: boolean;
  title: string;
  badge?: string;
  titleAction?: TitleAction;
  sectionAction?: SectionAction;
  sectionHeader?: React.ReactNode;
  sectionBanner?: React.ReactNode;
  component: React.ReactNode;
  activeClass?: string;
}

interface MenuItemsScrollableContentProps {
  items: MenuItem[];
  activeTab: string;
}

const MenuItemsScrollableContent: React.FC<MenuItemsScrollableContentProps> = ({
  items,
  activeTab,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!!containerRef?.current) {
      const height =
        containerRef.current?.parentElement?.parentElement?.offsetHeight;

      containerRef.current.style.setProperty(
        "--container-height",
        `${height}px`
      ); // We want the last item to have the height of the container, so that the scroll works correctly - so we set the height of the container as a CSS variable
    }
  }, [containerRef?.current]);

  return (
    <div className={"menu-item-tab-scrollable-content"} ref={containerRef}>
      {items?.map(
        (item) =>
          !item.isDisabled && (
            <Fragment key={item.id}>
              <Element id={item.id} name={item.id}>
                <div
                  className={`menu-item-tab-content details-pane-section-${item.id} ${activeTab === item.id ? `is-active ${item.activeClass || ""}` : ""}`}
                  data-testid={`tab-content-${item.id}`}
                >
                  {!!item.sectionBanner && (
                    <div
                      className="menu-item-section-banner"
                      data-testid={`tab-section-banner-${item.id}`}
                    >
                      {item.sectionBanner}
                    </div>
                  )}
                  <div className="tab-title-container">
                    <h3 className="tab-title">{item.title}</h3>
                    {!!item.badge && (
                      <Label
                        text={item.badge}
                        variant={LabelVariant.Rect}
                        className={"tab-title-badge"}
                      />
                    )}
                    {!!item.titleAction && (
                      <IconButton
                        data-testid={`tab-title-action-${item.id}`}
                        isIcon={item.titleAction.icon}
                        onClick={item.titleAction.onClick}
                        withTooltip={{
                          content: item.titleAction.tooltipContent,
                        }}
                        size={"small"}
                        {...getDataAnalyticsProps(item.titleAction)}
                      />
                    )}
                    {!!item.sectionAction && (
                      <>
                        {item.sectionAction.isButton ? (
                          <TextButton
                            data-testid={`tab-section-action-${item.id}`}
                            leftIcon={item.sectionAction.icon}
                            label={item.sectionAction.label}
                            onClick={item.sectionAction.onClick}
                            withTooltip={{
                              content: item.sectionAction.tooltipContent,
                            }}
                            wrapperClassName={"tab-section-action"}
                            buttonStyle={"textLink"}
                            {...getDataAnalyticsProps(item.sectionAction)}
                          />
                        ) : (
                          <div className={"tab-section-action"}>
                            {item.sectionAction.component}
                          </div>
                        )}
                      </>
                    )}
                    {!!item.sectionHeader && (
                      <div className={"tab-section-action"}>
                        {item.sectionHeader}
                      </div>
                    )}
                  </div>
                  {item.component}
                </div>
              </Element>
            </Fragment>
          )
      )}
    </div>
  );
};

export default MenuItemsScrollableContent;
